import {FC, useContext, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Typography, FormControl, Select, MenuItem} from '@mui/material';
import {useTranslation} from 'react-i18next';
import isoCountries from 'i18n-iso-countries';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/pt';
import BlankCard from 'components/BlankCard/BlankCard';
import {authContext} from 'contextApi/context/auth';
import {
  GET_PUBLIC_HOLIDAYS,
  GET_CONFIGURED_COUNTRIES,
} from 'graphql/absences/Querys';
import './HolidaysCard.scss';

interface PublicHoliday {
  name: string;
  date: string;
}

interface CountryConfig {
  id: string;
  country: string;
}

const HolidaysCard: FC = () => {
  const {t, i18n} = useTranslation();
  const {userDetails} = useContext(authContext);
  const userCountry = userDetails?.data?.country as string | undefined;

  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    undefined
  );

  const {data: countriesData} = useQuery<{getConfigurations: CountryConfig[]}>(
    GET_CONFIGURED_COUNTRIES,
    {
      onCompleted: data => {
        if (!selectedCountry) {
          const defaultCountry =
            data.getConfigurations.find(c => c.country === userCountry)
              ?.country ?? data.getConfigurations[0]?.country;
          setSelectedCountry(defaultCountry);
        }
      },
    }
  );

  const activeCountry = selectedCountry ?? userCountry;

  const {data, loading, error} = useQuery<{publicHolidays: PublicHoliday[]}>(
    GET_PUBLIC_HOLIDAYS,
    {
      variables: {
        countryCode: activeCountry,
        year: new Date().getFullYear(),
      },
      skip: !activeCountry,
    }
  );

  const today = dayjs().startOf('day');

  const sorted = [...(data?.publicHolidays ?? [])].sort((a, b) =>
    dayjs(a.date).isBefore(dayjs(b.date)) ? -1 : 1
  );

  const countries = countriesData?.getConfigurations ?? [];

  const cardTitle =
    countries.length > 1 ? (
      <span className="HolidaysCard__header">
        <span>{t('leaves.HolidaysCard.title')}</span>
        <FormControl size="small">
          <Select
            value={activeCountry ?? ''}
            onChange={e => setSelectedCountry(e.target.value)}
            className="HolidaysCard__select"
          >
            {countries.map(c => (
              <MenuItem key={c.id} value={c.country}>
                {isoCountries.getName(c.country, i18n.language) ?? c.country}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </span>
    ) : (
      t('leaves.HolidaysCard.title')
    );

  return (
    <BlankCard title={cardTitle}>
      {!activeCountry ||
        (!loading && !error && sorted.length === 0 && (
          <Typography>{t('leaves.HolidaysCard.emptyMessage')}</Typography>
        ))}
      {activeCountry && loading && (
        <Typography>{t('leaves.HolidaysCard.loadingMessage')}</Typography>
      )}
      {!!error && (
        <Typography>{t('leaves.HolidaysCard.errorMessage')}</Typography>
      )}
      {activeCountry && sorted.length > 0 && (
        <ul className="HolidaysCard__list">
          {sorted.map(holiday => {
            const isPast = dayjs(holiday.date).isBefore(today);
            return (
              <li
                key={holiday.date}
                className={`HolidaysCard__item${
                  isPast ? ' HolidaysCard__item--past' : ''
                }`}
              >
                <Typography variant="body2">{holiday.name}</Typography>
                <Typography variant="caption">
                  {dayjs(holiday.date)
                    .locale(i18n.language)
                    .format('DD MMM YYYY')}
                </Typography>
              </li>
            );
          })}
        </ul>
      )}
    </BlankCard>
  );
};

export default HolidaysCard;
